/**
 * تب روی `radix-ui`.
 *
 * دلیل استفاده از Radix و نه پیاده‌سازی دستی: تب درست باید با کلیدهای جهت‌دار
 * ناوبری کند، `role="tablist"` و `aria-selected` بدهد و focus را مدیریت کند.
 * پیاده‌سازی دستی تقریباً همیشه یکی از این‌ها را از دست می‌دهد. Radix جهت
 * راست‌به‌چپ را هم از `DirectionProvider` می‌گیرد، پس کلید چپ و راست برعکس
 * نمی‌شود.
 *
 * ظاهر: کنترل قطعه‌ای (segmented) روی یک بستر فرورفته، نه خط زیرِ نازک. قطعه‌ی
 * فعال یک سطح برجسته است و همان جایی که چشم انتظار دارد «فشرده» دیده می‌شود.
 */

import { Tabs as RadixTabs } from 'radix-ui'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface TabItem {
  value: string
  label: string
  icon?: ReactNode
  content: ReactNode
}

interface TabsProps {
  items: TabItem[]
  value: string
  onValueChange: (value: string) => void
  className?: string
}

export const Tabs = ({ items, value, onValueChange, className }: TabsProps) => (
  <RadixTabs.Root value={value} onValueChange={onValueChange} className={className}>
    <RadixTabs.List
      className={cn(
        'scrollbar-thin inline-flex max-w-full gap-1 overflow-x-auto',
        'rounded-lg border border-line bg-surface-sunken p-1',
      )}
    >
      {items.map((item) => (
        <RadixTabs.Trigger
          key={item.value}
          value={item.value}
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-md px-3.5 py-2',
            'text-sm whitespace-nowrap text-ink-muted transition-all duration-150',
            'hover:text-ink',
            'data-[state=active]:bg-surface-raised data-[state=active]:font-semibold',
            'data-[state=active]:text-accent data-[state=active]:shadow-raised',
          )}
        >
          {item.icon}
          {item.label}
        </RadixTabs.Trigger>
      ))}
    </RadixTabs.List>
    {items.map((item) => (
      <RadixTabs.Content
        key={item.value}
        value={item.value}
        // ورود نرم محتوا، تا تعویض تب «پرش» دیده نشود.
        className="animate-fade pt-5 focus-visible:outline-none"
      >
        {item.content}
      </RadixTabs.Content>
    ))}
  </RadixTabs.Root>
)
